import { useQuery } from "@tanstack/react-query";
import { format, differenceInMonths, eachDayOfInterval, isAfter, startOfDay } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { decorateReceivables, type ReceivableOrigin, type StayPlanChargeLink } from "@/lib/daycare-hotel/receivableOrigins";
import { STAY_PLAN_TABLES } from "@/lib/daycare-hotel/stayPlans";
import { abcCurve } from "@/lib/reports/abc";
import {
  compareReceivables,
  paymentMethodTotals,
  returnsBySale,
  saleNet,
  type RefundEntryRow,
  type SalePaymentRow,
} from "@/lib/reports/revenueReconciliation";

export interface DailyRevenuePoint {
  date: string;
  realized: number;
  previous: number;
  projected?: number;
}

export interface PaymentMethodRow {
  method: string;
  amount: number;
  percentage: number;
}

export interface CategoryRevenueRow {
  id: string;
  name: string;
  color: string;
  revenue: number;
  cost: number;
  margin: number;
  pct: number;
}

export interface ReceivableRow {
  saleId: string;
  saleNumber: string;
  ownerName: string;
  petName: string;
  createdAt: string;
  total: number;
  status: "ABERTA" | "RASCUNHO";
  /** Vencimento da charge do plano; `null` em venda avulsa. */
  dueOn: string | null;
  origin: ReceivableOrigin | null;
  isOverdue: boolean;
}

export interface AbcPetRow {
  petId: string;
  petName: string;
  ownerName: string;
  speciesName: string;
  totalRevenue: number;
  saleCount: number;
  ltv: number;
  firstSaleAt: string | null;
  abc: "A" | "B" | "C";
  cumulativePct: number;
}

export interface RevenueKpiSet {
  grossRevenue: number;
  netRevenue: number;
  serviceRevenue: number;
  productRevenue: number;
  sparkline: number[];
}

export interface RevenueReportData {
  current: RevenueKpiSet;
  previous: RevenueKpiSet;
  dailyRevenue: DailyRevenuePoint[];
  paymentMethods: PaymentMethodRow[];
  categoryRevenue: CategoryRevenueRow[];
  receivables: ReceivableRow[];
  abcPets: AbcPetRow[];
  totalReceivables: number;
}

type ReturnsBySale = Map<string, { subtotal: number; total: number }>;

// Colunas do Financeiro mais novas que types.ts (gerado, não se edita à mão): leitura sem tipo.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untyped = supabase as any;

/** Devoluções concluídas das vendas: a receita do relatório desconta o reembolso, como o Financeiro. */
async function fetchReturnsBySale(saleIds: string[]): Promise<ReturnsBySale> {
  if (saleIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from("sale_returns")
    .select("sale_id, subtotal, total")
    .in("sale_id", saleIds)
    .eq("status", "COMPLETED");
  if (error) throw error;
  return returnsBySale(data ?? []);
}

/** Reembolsos lançados no razão para essas vendas, com os estornos deles. */
async function fetchRefundEntries(saleIds: string[]): Promise<RefundEntryRow[]> {
  if (saleIds.length === 0) return [];
  const { data, error } = await untyped
    .from("financial_entries")
    .select("id, amount, payment_method, sale_return_id, reversal_of")
    .in("sale_id", saleIds)
    .is("voided_at", null)
    .or("sale_return_id.not.is.null,reversal_of.not.is.null");
  if (error) throw error;
  return (data ?? []) as RefundEntryRow[];
}

async function fetchKpiSet(
  clinicId: string,
  fromIso: string,
  toIso: string
): Promise<Omit<RevenueKpiSet, "sparkline">> {
  const { data: sales, error: salesErr } = await supabase
    .from("sales")
    .select("id, total, subtotal, discount_amount")
    .eq("clinic_id", clinicId)
    .eq("status", "PAGA")
    .gte("created_at", fromIso)
    .lte("created_at", toIso);
  if (salesErr) throw salesErr;

  const saleIds = (sales ?? []).map((s) => s.id);
  let serviceRevenue = 0;
  let productRevenue = 0;

  if (saleIds.length > 0) {
    const { data: saleItemsData, error: siErr } = await supabase
      .from("sale_items")
      .select("sale_id, item_id, line_total, quantity")
      .in("sale_id", saleIds);
    if (siErr) throw siErr;
    const saleItems = saleItemsData ?? [];

    const itemIds = [...new Set(saleItems.map((i) => i.item_id).filter(Boolean))] as string[];
    const itemTypes: Record<string, string> = {};
    if (itemIds.length > 0) {
      const { data: items } = await supabase
        .from("items")
        .select("id, type")
        .in("id", itemIds);
      (items ?? []).forEach((it: { id: string; type: string }) => {
        itemTypes[it.id] = it.type;
      });
    }

    saleItems.forEach((si) => {
      const t = itemTypes[si.item_id ?? ""] ?? "SERVICO";
      if (t === "PRODUTO") productRevenue += si.line_total ?? 0;
      else serviceRevenue += si.line_total ?? 0;
    });
  }

  // devolução só existe para produto: sai da receita bruta e da de produto pelo valor das linhas,
  // e da líquida pelo reembolso (com o desconto da venda rateado)
  const returns = await fetchReturnsBySale(saleIds);
  const returned = [...returns.values()].reduce((s, r) => s + r.subtotal, 0);
  productRevenue -= returned;
  const grossRevenue = (sales ?? []).reduce((s, x) => s + Number(x.subtotal ?? x.total ?? 0), 0) - returned;
  const netRevenue = (sales ?? []).reduce((s, x) => s + saleNet(x, returns), 0);

  return { grossRevenue, netRevenue, serviceRevenue, productRevenue };
}

async function fetchDailyRevenue(
  clinicId: string,
  fromIso: string,
  toIso: string
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("sales")
    .select("id, created_at, total")
    .eq("clinic_id", clinicId)
    .eq("status", "PAGA")
    .gte("created_at", fromIso)
    .lte("created_at", toIso);
  if (error) throw error;
  const returns = await fetchReturnsBySale((data ?? []).map((s) => s.id));
  const byDay: Record<string, number> = {};
  (data ?? []).forEach((s) => {
    const day = format(new Date(s.created_at!), "yyyy-MM-dd");
    byDay[day] = (byDay[day] ?? 0) + saleNet(s, returns);
  });
  return byDay;
}

export function useRevenueReport(
  dateFrom: Date | null,
  dateTo: Date | null,
  compareFrom: Date | null,
  compareTo: Date | null
) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: [
      "revenue-report",
      clinicId,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      compareFrom?.toISOString(),
      compareTo?.toISOString(),
    ],
    enabled: !!clinicId && !!dateFrom && !!dateTo,
    queryFn: async (): Promise<RevenueReportData> => {
      const fromIso = dateFrom!.toISOString();
      const toIso = dateTo!.toISOString();
      const cFromIso = compareFrom?.toISOString() ?? null;
      const cToIso = compareTo?.toISOString() ?? null;

      // KPIs (parallel)
      const [currentKpi, previousKpi] = await Promise.all([
        fetchKpiSet(clinicId!, fromIso, toIso),
        cFromIso && cToIso
          ? fetchKpiSet(clinicId!, cFromIso, cToIso)
          : Promise.resolve<Omit<RevenueKpiSet, "sparkline">>({
              grossRevenue: 0,
              netRevenue: 0,
              serviceRevenue: 0,
              productRevenue: 0,
            }),
      ]);

      // Sparklines: 7 equal buckets across the period
      const allDays = eachDayOfInterval({ start: dateFrom!, end: dateTo! });
      const step = Math.max(1, Math.floor(allDays.length / 7));
      const buckets = Array.from({ length: 7 }, (_, i) => {
        const from = allDays[Math.min(i * step, allDays.length - 1)];
        const to = allDays[Math.min((i + 1) * step - 1, allDays.length - 1)];
        return { from, to };
      });
      const { data: salesForSpark } = await supabase
        .from("sales")
        .select("id, created_at, total, subtotal")
        .eq("clinic_id", clinicId!)
        .eq("status", "PAGA")
        .gte("created_at", fromIso)
        .lte("created_at", toIso);
      const sparkReturns = await fetchReturnsBySale((salesForSpark ?? []).map((s) => s.id));
      const sparkNet = buckets.map(({ from, to }) =>
        (salesForSpark ?? [])
          .filter((s) => {
            const d = new Date(s.created_at!);
            return d >= from && d <= to;
          })
          .reduce((sum, s) => sum + saleNet(s, sparkReturns), 0)
      );

      // Daily revenue for chart
      const today = startOfDay(new Date());
      const [dailyCur, dailyPrev] = await Promise.all([
        fetchDailyRevenue(clinicId!, fromIso, toIso),
        cFromIso && cToIso
          ? fetchDailyRevenue(clinicId!, cFromIso, cToIso)
          : Promise.resolve<Record<string, number>>({}),
      ]);

      const prevDays =
        cFromIso && cToIso
          ? eachDayOfInterval({ start: compareFrom!, end: compareTo! })
          : [];

      const daysElapsed = allDays.filter((d) => !isAfter(d, today)).length;
      const realizedTotal = Object.values(dailyCur).reduce((s, v) => s + v, 0);
      const dailyAvg = daysElapsed > 0 ? realizedTotal / daysElapsed : 0;

      const dailyRevenue: DailyRevenuePoint[] = allDays.map((day, idx) => {
        const key = format(day, "yyyy-MM-dd");
        const isFuture = isAfter(day, today);
        const prevDay = prevDays[idx];
        const prevKey = prevDay ? format(prevDay, "yyyy-MM-dd") : null;
        return {
          date: format(day, "dd/MM"),
          realized: isFuture ? 0 : (dailyCur[key] ?? 0),
          previous: prevKey ? (dailyPrev[prevKey] ?? 0) : 0,
          ...(isFuture ? { projected: dailyAvg } : {}),
        };
      });

      // Payment methods
      const { data: salesPaid } = await supabase
        .from("sales")
        .select("id, total")
        .eq("clinic_id", clinicId!)
        .eq("status", "PAGA")
        .gte("created_at", fromIso)
        .lte("created_at", toIso);
      const paidIds = (salesPaid ?? []).map((s) => s.id);
      let paymentMethods: PaymentMethodRow[] = [];
      const paidReturns = await fetchReturnsBySale(paidIds);
      if (paidIds.length > 0) {
        // o mesmo dinheiro do caixa: troco não é pagamento e reembolso sai da forma original
        const [{ data: payments, error: paymentsError }, refunds] = await Promise.all([
          supabase.from("sale_payments").select("sale_id, method, amount, created_at").in("sale_id", paidIds),
          fetchRefundEntries(paidIds),
        ]);
        if (paymentsError) throw paymentsError;
        paymentMethods = paymentMethodTotals(salesPaid ?? [], (payments ?? []) as SalePaymentRow[], refunds);
      }

      // Category revenue by clinic_appointment_type
      const { data: catTypes } = await supabase
        .from("clinic_appointment_types")
        .select("id, name, color")
        .eq("clinic_id", clinicId!)
        .eq("active", true);

      const catAgg: Record<string, { revenue: number; cost: number }> = {};
      const catMeta: Record<string, { name: string; color: string }> = {};
      (catTypes ?? []).forEach((c: { id: string; name: string; color: string }) => {
        catAgg[c.id] = { revenue: 0, cost: 0 };
        catMeta[c.id] = { name: c.name, color: c.color };
      });
      catAgg["__other__"] = { revenue: 0, cost: 0 };
      catMeta["__other__"] = { name: "Outros", color: "#8a7e74" };

      if (paidIds.length > 0) {
        type ApptType = { id: string; name: string; color: string } | null;
        type ApptRow = { clinic_appointment_type: ApptType } | null;
        type SaleWithAppt = {
          id: string;
          total: number | null;
          appointment_id: string | null;
          appointments: ApptRow;
        };

        const { data: salesWithAppt } = await supabase
          .from("sales")
          .select(
            "id, total, appointment_id, appointments!appointment_id(clinic_appointment_type:clinic_appointment_types(id, name, color))"
          )
          .eq("clinic_id", clinicId!)
          .eq("status", "PAGA")
          .gte("created_at", fromIso)
          .lte("created_at", toIso);

        const saleCatMap: Record<string, string> = {};
        (salesWithAppt as SaleWithAppt[] ?? []).forEach((s) => {
          const catId =
            (s.appointments as ApptRow)?.clinic_appointment_type?.id ?? "__other__";
          if (!(catId in catAgg)) catAgg[catId] = { revenue: 0, cost: 0 };
          catAgg[catId].revenue += saleNet(s, paidReturns);
          saleCatMap[s.id] = catId;
        });

        type SaleItemWithCost = {
          sale_id: string;
          line_total: number;
          quantity: number;
          item_id: string | null;
          items: { cost_price: number | null; type: string } | null;
        };

        const { data: saleItemsWithCost } = await supabase
          .from("sale_items")
          .select("sale_id, line_total, quantity, item_id, items!item_id(cost_price, type)")
          .in("sale_id", paidIds);

        (saleItemsWithCost as SaleItemWithCost[] ?? []).forEach((si) => {
          const catId = saleCatMap[si.sale_id] ?? "__other__";
          if (!(catId in catAgg)) catAgg[catId] = { revenue: 0, cost: 0 };
          if (si.items?.type === "PRODUTO" && si.items.cost_price != null) {
            catAgg[catId].cost += si.items.cost_price * si.quantity;
          }
        });
      }

      const totalCatRevenue = Object.values(catAgg).reduce((s, v) => s + v.revenue, 0);
      const categoryRevenue: CategoryRevenueRow[] = Object.entries(catAgg)
        .filter(([, v]) => v.revenue > 0)
        .map(([id, v]) => ({
          id,
          name: catMeta[id]?.name ?? "Outros",
          color: catMeta[id]?.color ?? "#8a7e74",
          revenue: v.revenue,
          cost: v.cost,
          margin: v.revenue > 0 ? ((v.revenue - v.cost) / v.revenue) * 100 : 0,
          pct: totalCatRevenue > 0 ? (v.revenue / totalCatRevenue) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // Receivables. A fonte da verdade do "a receber" de venda é o título no Financeiro (o PDV grava a
      // venda PAGA com um pagamento A_RECEBER). Comandas ABERTAS (mensalidade de creche/hotel) ainda não
      // geram título: continuam vindo da venda.
      const fromDay = format(dateFrom!, "yyyy-MM-dd");
      const toDay = format(dateTo!, "yyyy-MM-dd");
      const [{ data: openSales }, { data: openTitles, error: titlesError }] = await Promise.all([
        supabase
          .from("sales")
          .select("id, sale_number, owner_id, pet_id, created_at, total, status")
          .eq("clinic_id", clinicId!)
          .eq("status", "ABERTA")
          .gte("created_at", fromIso)
          .lte("created_at", toIso)
          .order("created_at", { ascending: true }),
        untyped
          .from("financial_titles")
          .select("sale_id, amount, due_date")
          .eq("clinic_id", clinicId!)
          .eq("kind", "receivable")
          .eq("status", "open")
          .not("sale_id", "is", null)
          .gte("competence_date", fromDay)
          .lte("competence_date", toDay),
      ]);
      if (titlesError) throw titlesError;
      type OpenTitle = { sale_id: string; amount: number; due_date: string };
      const titleRows = (openTitles ?? []) as OpenTitle[];
      const titleSaleIds = [...new Set(titleRows.map((t) => t.sale_id))];
      const { data: titleSales } = titleSaleIds.length
        ? await supabase.from("sales").select("id, sale_number, owner_id, pet_id, created_at").in("id", titleSaleIds)
        : { data: [] as Array<{ id: string; sale_number: string; owner_id: string | null; pet_id: string | null; created_at: string | null }> };
      const titleSaleById = new Map((titleSales ?? []).map((s) => [s.id, s]));
      const salesWithoutTitle = (openSales ?? []).filter((s) => !titleSaleById.has(s.id));

      const allOpen = [...salesWithoutTitle, ...(titleSales ?? [])];
      const openOwnerIds = [
        ...new Set(allOpen.map((s) => s.owner_id).filter(Boolean)),
      ] as string[];
      const openPetIds = [
        ...new Set(allOpen.map((s) => s.pet_id).filter(Boolean)),
      ] as string[];
      const ownerNames: Record<string, string> = {};
      const petNames: Record<string, string> = {};

      await Promise.all([
        openOwnerIds.length > 0
          ? supabase
              .from("owners")
              .select("id, name")
              .in("id", openOwnerIds)
              .then(({ data }) => {
                (data ?? []).forEach((o: { id: string; name: string }) => {
                  ownerNames[o.id] = o.name;
                });
              })
          : Promise.resolve(),
        openPetIds.length > 0
          ? supabase
              .from("pets")
              .select("id, name")
              .in("id", openPetIds)
              .then(({ data }) => {
                (data ?? []).forEach((p: { id: string; name: string }) => {
                  petNames[p.id] = p.name;
                });
              })
          : Promise.resolve(),
      ]);

      const openSaleIds = salesWithoutTitle.map((s) => s.id);
      const chargeLinks = new Map<string, StayPlanChargeLink>();
      if (openSaleIds.length > 0) {
        await Promise.all((["daycare", "hotel"] as const).map(async (module) => {
          const tables = STAY_PLAN_TABLES[module];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data } = await (supabase as any)
            .from(tables.charges)
            .select(`sale_id, due_on, contract:${tables.contracts}(id, pet_id, terms_snapshot)`)
            .in("sale_id", openSaleIds);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data ?? []).forEach((charge: any) => {
            if (!charge.sale_id) return;
            chargeLinks.set(charge.sale_id, {
              saleId: charge.sale_id,
              dueOn: charge.due_on,
              module,
              contractId: charge.contract?.id ?? "",
              petId: charge.contract?.pet_id ?? null,
              planName: charge.contract?.terms_snapshot?.name ?? "",
            });
          });
        }));
      }

      const todayIso = new Date().toLocaleDateString("en-CA");
      const nameOf = (s: { owner_id: string | null; pet_id: string | null }) => ({
        ownerName: s.owner_id ? (ownerNames[s.owner_id] ?? "—") : "—",
        petName: s.pet_id ? (petNames[s.pet_id] ?? "—") : "—",
      });
      const fromSales: ReceivableRow[] = decorateReceivables(
        salesWithoutTitle.map((s) => ({
          saleId: s.id,
          saleNumber: s.sale_number,
          ...nameOf(s),
          createdAt: s.created_at ?? "",
          total: Number(s.total ?? 0),
          status: (s.status ?? "ABERTA") as "ABERTA" | "RASCUNHO",
        })),
        chargeLinks,
        todayIso,
      );
      // um título por pagamento a receber: a venda aparece uma vez, com o que falta receber dela
      const titleBySale = new Map<string, { amount: number; dueOn: string }>();
      for (const t of titleRows) {
        const cur = titleBySale.get(t.sale_id);
        titleBySale.set(t.sale_id, {
          amount: (cur?.amount ?? 0) + Number(t.amount ?? 0),
          dueOn: cur && cur.dueOn < t.due_date ? cur.dueOn : t.due_date,
        });
      }
      const fromTitles: ReceivableRow[] = [...titleBySale.entries()].flatMap(([saleId, t]) => {
        const sale = titleSaleById.get(saleId);
        if (!sale) return [];
        return [{
          saleId,
          saleNumber: sale.sale_number,
          ...nameOf(sale),
          createdAt: sale.created_at ?? "",
          total: t.amount,
          status: "ABERTA" as const,
          dueOn: t.dueOn,
          origin: null,
          // como no Financeiro: vencer hoje ainda está no prazo
          isOverdue: t.dueOn < todayIso,
        }];
      });
      // Dívida de venda renegociada no Financeiro: vira parcelas sem venda ligada. Segue a dívida
      // (renegotiated_into → parcelas, que podem ter sido renegociadas de novo) até o que está em aberto.
      const { data: renegotiated, error: renegError } = await untyped
        .from("financial_titles")
        .select("description, counterparty_name, competence_date, renegotiated_into")
        .eq("clinic_id", clinicId!)
        .eq("kind", "receivable")
        .eq("cancel_reason", "renegotiated")
        .not("sale_id", "is", null)
        .not("renegotiated_into", "is", null)
        .gte("competence_date", fromDay)
        .lte("competence_date", toDay);
      if (renegError) throw renegError;
      type Renegotiated = { description: string; counterparty_name: string; competence_date: string; renegotiated_into: string };
      type Installment = { installment_group_id: string; amount: number; due_date: string; status: string; renegotiated_into: string | null };
      const fromRenegotiations: ReceivableRow[] = [];
      const bySaleDebt = new Map<string, Renegotiated[]>();
      for (const r of (renegotiated ?? []) as Renegotiated[]) {
        bySaleDebt.set(r.renegotiated_into, [...(bySaleDebt.get(r.renegotiated_into) ?? []), r]);
      }
      for (const [group, originals] of bySaleDebt) {
        let open = 0;
        let dueOn: string | null = null;
        let frontier = [group];
        for (let depth = 0; frontier.length > 0 && depth < 5; depth++) {
          const { data: parts, error: partsError } = await untyped
            .from("financial_titles")
            .select("installment_group_id, amount, due_date, status, renegotiated_into")
            .in("installment_group_id", frontier);
          if (partsError) throw partsError;
          const rows = (parts ?? []) as Installment[];
          for (const part of rows.filter((x) => x.status === "open")) {
            open += Number(part.amount ?? 0);
            if (!dueOn || part.due_date < dueOn) dueOn = part.due_date;
          }
          frontier = [...new Set(rows.map((x) => x.renegotiated_into).filter((x): x is string => !!x))];
        }
        if (open <= 0) continue;
        fromRenegotiations.push({
          saleId: `renegotiation:${group}`,
          saleNumber: originals.map((o) => o.description).join(", "),
          ownerName: originals[0].counterparty_name ?? "—",
          petName: "—",
          createdAt: originals[0].competence_date,
          total: Math.round(open * 100) / 100,
          status: "ABERTA",
          dueOn,
          origin: null,
          isOverdue: !!dueOn && dueOn < todayIso,
        });
      }

      const receivables = [...fromSales, ...fromTitles, ...fromRenegotiations].sort(compareReceivables);
      const totalReceivables = receivables.reduce((s, r) => s + r.total, 0);

      // ABC Pets
      const { data: salesForAbc } = await supabase
        .from("sales")
        .select("id, pet_id, total, created_at")
        .eq("clinic_id", clinicId!)
        .eq("status", "PAGA")
        .gte("created_at", fromIso)
        .lte("created_at", toIso)
        .not("pet_id", "is", null);

      const petAgg: Record<
        string,
        { total: number; count: number; firstAt: string; lastAt: string }
      > = {};
      (salesForAbc ?? []).forEach((s) => {
        const pid = s.pet_id!;
        if (!petAgg[pid]) {
          petAgg[pid] = {
            total: 0,
            count: 0,
            firstAt: s.created_at ?? "",
            lastAt: s.created_at ?? "",
          };
        }
        petAgg[pid].total += saleNet(s, paidReturns);
        petAgg[pid].count += 1;
        if ((s.created_at ?? "") < petAgg[pid].firstAt)
          petAgg[pid].firstAt = s.created_at ?? "";
        if ((s.created_at ?? "") > petAgg[pid].lastAt)
          petAgg[pid].lastAt = s.created_at ?? "";
      });

      const abcPetIds = Object.keys(petAgg);
      const abcPetNames: Record<string, string> = {};
      const abcPetOwnerIds: Record<string, string> = {};
      const abcPetSpecies: Record<string, string> = {};
      const abcOwnerNames: Record<string, string> = {};

      if (abcPetIds.length > 0) {
        type PetWithSpecies = {
          id: string;
          name: string;
          owner_id: string;
          species: { name: string } | null;
        };
        const { data: petsData } = await supabase
          .from("pets")
          .select("id, name, owner_id, species:species_id(name)")
          .in("id", abcPetIds)
          .is("deleted_at", null);
        (petsData as PetWithSpecies[] ?? []).forEach((p) => {
          abcPetNames[p.id] = p.name;
          abcPetOwnerIds[p.id] = p.owner_id;
          abcPetSpecies[p.id] = p.species?.name ?? "";
        });

        const ownerIdsForAbc = [...new Set(Object.values(abcPetOwnerIds))];
        if (ownerIdsForAbc.length > 0) {
          const { data: ownersData } = await supabase
            .from("owners")
            .select("id, name")
            .in("id", ownerIdsForAbc);
          (ownersData ?? []).forEach((o: { id: string; name: string }) => {
            abcOwnerNames[o.id] = o.name;
          });
        }
      }

      const sorted = Object.entries(petAgg).sort((a, b) => b[1].total - a[1].total);
      const grandTotal = sorted.reduce((s, [, v]) => s + v.total, 0);
      const curve = abcCurve(sorted.map(([, agg]) => agg.total));

      const abcPets: AbcPetRow[] = sorted.map(([petId, agg], index) => {
        const cumulativePct = grandTotal > 0 ? curve[index].cumulativeShare * 100 : 0;
        const abc = curve[index].abc;
        const ownerId = abcPetOwnerIds[petId] ?? "";
        const monthsActive = Math.max(
          1,
          differenceInMonths(new Date(agg.lastAt), new Date(agg.firstAt)) + 1
        );
        const ltv = (agg.total / monthsActive) * 24;
        return {
          petId,
          petName: abcPetNames[petId] ?? "—",
          ownerName: abcOwnerNames[ownerId] ?? "—",
          speciesName: abcPetSpecies[petId] ?? "",
          totalRevenue: agg.total,
          saleCount: agg.count,
          ltv,
          firstSaleAt: agg.firstAt,
          abc,
          cumulativePct,
        };
      });

      return {
        current: { ...currentKpi, sparkline: sparkNet },
        previous: { ...previousKpi, sparkline: [] },
        dailyRevenue,
        paymentMethods,
        categoryRevenue,
        receivables,
        abcPets,
        totalReceivables,
      };
    },
  });
}
